import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { RichTextEditor, LoaderCircle, Popup, Select, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useAdminAccess } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import type { EmployeeConsent } from '../resources'
import adminResources from '../resources'

import { ConsentRow } from './ConsentRow'

import './index.styles.scss'

export const EmployeeConsents: FC = () => {
  useAdminAccess()

  const profile = useProfile()

  const navigate = useNavigate()
  const translate = useTranslation()
  const isHeadAdmin = Boolean(useUser()?.role === 'customer_head_admin')
  const isAdmin = Boolean(useUser()?.role === 'admin')
  const isCustomerSelectionObligatory = isHeadAdmin || isAdmin
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedConsentId, setSelectedConsentId] = useState<number | null>(null)
  const [actionType, setActionType] = useState<'create' | 'copy' | 'edit'>('create')

  useEffect(() => {
    if (!profile?.featureAccess.consent) {
      navigate('/admin/features#consent')
    }
  }, [navigate, profile?.featureAccess.consent])

  const { data, isLoading } = useQuery(queries.adminConsents(), adminResources.getConsents)
  const addEmployeeConsent = useMutation(adminResources.postEmployeeConsent, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminConsents())
      handleClose()
    },
  })
  const updateEmployeeConsent = useMutation(adminResources.updateEmployeeConsent, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminConsents())
      handleClose()
    },
  })
  const { data: customers = [] } = useQuery(
    queries.adminCustomersWithActiveFeature('consent'),
    () => adminResources.getCustomersWithActiveFeature('consent')
  )

  const newestConsentIdByCustomerId = customers?.reduce<Record<number, EmployeeConsent | null>>(
    (acc, customer) => {
      // Items are sorted chronologically
      const newestCustomerItem =
        data?.find(
          consent =>
            consent.customer?.id === customer.id &&
            consent.employeeConsentStatus.name === 'Published'
        ) || null

      acc[customer.id] = newestCustomerItem

      return acc
    },
    {}
  )

  if (isLoading) return <LoaderCircle />

  function handleClose() {
    setIsModalOpen(false)
    setSelectedConsentId(null)
    setActionType('create')
  }

  const consentToCopy = selectedConsentId
    ? data?.find(consent => consent.id === selectedConsentId)
    : null

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)} className="employee-consents">
      <Container.Header>
        <Translation id="Employee consent" />
      </Container.Header>

      <Button Icon={<PlusIcon />} className="mb-4 ml-0" onClick={() => setIsModalOpen(true)}>
        <Translation id="New employee consent" />
      </Button>

      <div className="consent-list">
        {data?.map(consent => (
          <ConsentRow
            key={consent.id}
            consent={consent}
            newestConsentIdByCustomerId={newestConsentIdByCustomerId}
            onCopyConsent={(consentId: number) => {
              setSelectedConsentId(consentId)
              setActionType('copy')
              setIsModalOpen(true)
            }}
            onEditConsent={(consentId: number) => {
              setSelectedConsentId(consentId)
              setActionType('edit')
              setIsModalOpen(true)
            }}
          />
        ))}
      </div>

      <Popup
        ariaLabel={translate('Add a new employee consent')}
        isOpen={isModalOpen}
        onClose={handleClose}
        variant="wide"
      >
        <h2>
          {actionType === 'create' && <Translation id="Add new employee consent" />}
          {actionType === 'copy' && <Translation id="Copy employee consent" />}
          {actionType === 'edit' && <Translation id="Edit employee consent" />}
        </h2>

        <Formik
          initialValues={{
            title: consentToCopy?.title || '',
            text: consentToCopy?.text || '',
            customerId: consentToCopy?.customer?.id || '',
          }}
          validate={values => {
            const errors: Partial<{ text: string; customerId?: string }> = {}

            if (!values.text) errors.text = translate('This field is mandatory')
            if (!values.customerId && isCustomerSelectionObligatory)
              errors.customerId = translate(
                'Consent must always be connected to a given customer due to possible differences in prices and legislation systems.'
              )

            return errors
          }}
          onSubmit={formValues => {
            if (actionType === 'edit' && selectedConsentId) {
              updateEmployeeConsent.mutate({
                text: formValues.text,
                id: selectedConsentId,
                title: formValues.title,
              })
            } else {
              addEmployeeConsent.mutate({
                text: formValues.text,
                title: formValues.title,
                customerId: isCustomerSelectionObligatory
                  ? Number(formValues.customerId)
                  : undefined,
              })
            }
          }}
        >
          <div className="employee-consent-new">
            <Form>
              {isCustomerSelectionObligatory && (
                <Field name="customerId">
                  {({ field, meta, form }: FieldProps) => (
                    <Select
                      capitalizeLabel
                      id="customerId"
                      label={
                        <>
                          <Translation id="Customer" />*
                        </>
                      }
                      options={customers.map(customerListItem => ({
                        value: customerListItem.id,
                        label: customerListItem.name,
                      }))}
                      marginBottom={16}
                      touched={meta.touched}
                      error={meta.error}
                      {...field}
                      onChange={option => {
                        form.setFieldValue(field.name, option?.value)
                      }}
                      isDisabled={actionType === 'edit'}
                    />
                  )}
                </Field>
              )}
              <Field name="title">
                {({ field, meta }: FieldProps) => (
                  <Input
                    id="title"
                    isFullWidth
                    label={
                      <>
                        <Translation id="Consent title" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>
              <Field name="text">
                {({ field, meta, form }: FieldProps) => (
                  <div>
                    <RichTextEditor
                      initialHtml={field.value || ''}
                      onChange={html => {
                        // Update Formik field value with HTML
                        form.setFieldValue(field.name, html)
                        // Mark field as touched
                        form.setFieldTouched(field.name, true)
                      }}
                    />
                    {meta.touched && meta.error && (
                      <div className="mt-1 text-sm text-red-500">{meta.error}</div>
                    )}
                  </div>
                )}
              </Field>

              <footer>
                <Button
                  variant="tertiary"
                  disabled={addEmployeeConsent.isLoading}
                  onClick={handleClose}
                >
                  <Translation id="Cancel" />
                </Button>

                <Button type="submit" isLoading={addEmployeeConsent.isLoading}>
                  <Translation id="Save draft" />
                </Button>
              </footer>
            </Form>
          </div>
        </Formik>
      </Popup>
    </Container>
  )
}
